import React, { useState } from "react";
import axios from "../../utils/axiosConfig";
import { useLookup } from "../../utils/useLookup";
import { printPortraitReport } from "../../utils/printReport";
import { formatReportDate, formatReportDateRange } from "../../utils/reportDate";
import "./SalesReport.css";

const toDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const today = new Date();
const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);

const formatNumber = (value, decimals = 2) => Number(value || 0).toLocaleString("en-IN", {
  minimumFractionDigits: decimals,
  maximumFractionDigits: decimals
});

const formatOptionalNumber = (value, decimals = 2) => (
  value === null || value === undefined || value === ""
    ? "-"
    : formatNumber(value, decimals)
);

const formatPrice = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const values = String(value).split(",").map((part) => part.trim()).filter(Boolean);
  return values.map((part) => {
    const numericValue = Number(part);
    return Number.isNaN(numericValue) ? part : formatNumber(numericValue);
  }).join(", ");
};

const SalesReport = () => {
  const { suppliers, buyers } = useLookup();
  const [filterOption, setFilterOption] = useState("All");
  const [supplierId, setSupplierId] = useState("");
  const [buyerId, setBuyerId] = useState("");
  const [fromDate, setFromDate] = useState(toDateInput(currentMonthStart));
  const [toDate, setToDate] = useState(toDateInput(today));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleGenerate = async () => {
    setError("");

    if (filterOption === "Supplier" && !supplierId) {
      setError("Select a supplier.");
      return;
    }
    if (filterOption === "Buyer" && !buyerId) {
      setError("Select a buyer.");
      return;
    }
    if (!fromDate || !toDate) {
      setError("Select both from date and to date.");
      return;
    }
    if (fromDate > toDate) {
      setError("From date cannot be after to date.");
      return;
    }

    try {
      setLoading(true);
      const body = { filterOption, fromDate, toDate };
      if (filterOption === "Supplier") body.shipperId = Number(supplierId);
      if (filterOption === "Buyer") body.buyerId = Number(buyerId);

      const response = await axios.post("/report/sales-report", body);
      setReport(response.data);
    } catch (err) {
      setReport(null);
      setError(err.response?.data?.message || "Failed to generate report.");
    } finally {
      setLoading(false);
    }
  };

  const company = report?.company || {};
  const rows = report?.rows || [];
  const totals = report?.totals || {};

  return (
    <div className="container-fluid mt-3 sales-report-page">
      <div className="card shadow-sm border-0 no-print">
        <div className="card-header bg-white">
          <span className="badge bg-success fs-6 px-3 py-2">Sales Report</span>
        </div>
        <div className="card-body">
          <div className="border rounded p-3 bg-light">
            <div className="row mb-2 align-items-center sales-filter-row">
              <label className="col-sm-2 text-end">Select Filter Option</label>
              <span className="col-auto px-1">:</span>
              <div className="col-sm-6 d-flex gap-3 flex-wrap">
                {["All", "Supplier", "Buyer"].map((option) => (
                  <div className="form-check mb-0" key={option}>
                    <input
                      className="form-check-input"
                      type="radio"
                      id={`sales-filter-${option}`}
                      checked={filterOption === option}
                      onChange={() => {
                        setFilterOption(option);
                        setSupplierId("");
                        setBuyerId("");
                      }}
                    />
                    <label className="form-check-label" htmlFor={`sales-filter-${option}`}>{option}</label>
                  </div>
                ))}
              </div>
            </div>

            {filterOption === "Supplier" && (
              <div className="row mb-2 align-items-center sales-filter-row">
                <label className="col-sm-2 text-end">Select Supplier</label>
                <span className="col-auto px-1">:</span>
                <div className="col-sm-4">
                  <select className="form-select form-select-sm" value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
                    <option value="">Select Supplier</option>
                    {suppliers.map((supplier) => (
                      <option key={supplier.ShipperID} value={supplier.ShipperID}>{supplier.ShipperName}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {filterOption === "Buyer" && (
              <div className="row mb-2 align-items-center sales-filter-row">
                <label className="col-sm-2 text-end">Select Buyer</label>
                <span className="col-auto px-1">:</span>
                <div className="col-sm-4">
                  <select className="form-select form-select-sm" value={buyerId} onChange={(e) => setBuyerId(e.target.value)}>
                    <option value="">Select Buyer</option>
                    {buyers.map((buyer) => (
                      <option key={buyer.BuyerID} value={buyer.BuyerID}>{buyer.BuyerName}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div className="row mb-2 align-items-center sales-filter-row">
              <label className="col-sm-2 text-end">Deal Date</label>
              <span className="col-auto px-1">:</span>
              <div className="col-sm-2">
                <label className="form-label small mb-1" htmlFor="salesFromDate">From Date</label>
                <input id="salesFromDate" type="date" className="form-control form-control-sm" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              </div>
              <div className="col-sm-2">
                <label className="form-label small mb-1" htmlFor="salesToDate">To Date</label>
                <input id="salesToDate" type="date" className="form-control form-control-sm" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </div>
            </div>

            {error && <div className="alert alert-danger py-2 mt-3 mb-0">{error}</div>}

            <div className="row mt-3">
              <div className="col-sm-10 offset-sm-2 d-flex gap-2">
                <button className="btn btn-success btn-sm px-4" onClick={handleGenerate} disabled={loading}>
                  {loading ? "Generating..." : "Generate Report"}
                </button>
                {report && (
                  <button className="btn btn-outline-secondary btn-sm px-4" onClick={printPortraitReport}>
                    Print / Save PDF
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {report && (
        <section className="sales-report-print portrait-report-page mt-3">
          <header className="sales-report-header">
            <h1>{company.CompanyName || "Global Metals"}</h1>
            <h2>Sales Report</h2>
          </header>

          <div className="sales-report-meta">
            <span><strong>Filter:</strong> {report.criteria.filter}</span>
            <span><strong>Range:</strong> {formatReportDateRange(report.criteria.range)}</span>
            <span><strong>Generated:</strong> {formatReportDate(report.criteria.generatedAt)}</span>
          </div>

          <table className="sales-report-table">
            <thead>
              <tr>
                <th>Deal Date</th>
                <th>Deal ID</th>
                <th>SC No</th>
                <th>SC Date</th>
                <th>Supplier</th>
                <th>Buyer</th>
                <th>Materials</th>
                <th className="number">Price</th>
                <th className="number">Commission PMT</th>
                <th className="number">No. Of Container</th>
                <th>Container Size</th>
                <th className="number">Qty</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan="12" className="empty-report">No records found.</td></tr>
              ) : rows.map((row, index) => (
                <tr key={`${row.DealID}-${index}`}>
                  <td>{formatReportDate(row.DealDate)}</td>
                  <td>{row.DealNo || row.DealID}</td>
                  <td>{row.SCNo || "-"}</td>
                  <td>{formatReportDate(row.SCDate)}</td>
                  <td>{row.ShipperName || "-"}</td>
                  <td>{row.BuyerName || "-"}</td>
                  <td>{row.Materials || "-"}</td>
                  <td className="number">{formatPrice(row.Price)}</td>
                  <td className="number">{formatOptionalNumber(row.CommissionPMT)}</td>
                  <td className="number">{formatNumber(row.NoOfContainer, 0)}</td>
                  <td>{row.ContainerSize || "-"}</td>
                  <td className="number">{formatNumber(row.Qty, 3)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="9">Total ({rows.length} deals)</td>
                <td className="number">{formatNumber(totals.containers, 0)}</td>
                <td />
                <td className="number">{formatNumber(totals.quantity, 3)}</td>
              </tr>
            </tfoot>
          </table>
        </section>
      )}
    </div>
  );
};

export default SalesReport;
